import {
  splitToneNumbersPinyinSyllables,
  toneNumberSyllableForComparison,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import arrayLength from "@-/com.npmjs.extra-array__length";

import type { ProducedToken, SentenceDrillToken } from "#sentence-drill-token";

/**
 * Canonical comparison form for a whole multi-syllable reading.
 *
 * Per-syllable rather than whole-string because `toneNumberSyllableForComparison` canonicalizes one
 * syllable at a time (neutral `5`→`0`, `v`→`ü`, case, NFC) and readings arrive both spaced
 * (`shui4 jiao4`) and unspaced (`shui4jiao4`) depending on their source. Splitting first makes the
 * two spellings compare equal, which they must: the same word carries both forms across the
 * dictionary tables and the drill route.
 */
function toneNumbersPinyinForComparison(toneNumbersPinyin: string): string | null {
  const syllables = splitToneNumbersPinyinSyllables(toneNumbersPinyin);
  if (arrayLength(syllables) === 0) {
    return null;
  }
  return syllables.map((syllable) => toneNumberSyllableForComparison(syllable)).join(" ");
}

/**
 * Does what the user produced count as the expected token?
 *
 * Two ways to match, in order:
 *
 *   1. **Exact characters.** The unambiguous case.
 *   2. **Pinyin equivalence.** The readings are equal syllable-for-syllable, tones included. This
 *      exists because speech recognition returns CHARACTERS, and Mandarin is dense with homophones:
 *      a user who says 苹果 perfectly may be transcribed 平果. Rejecting that would punish correct
 *      speech for a recognizer's spelling choice.
 *
 * Tones are deliberately NOT stripped — they are phonemic, and a drill that accepted `shi2` for
 * `shi4` would be teaching the wrong thing. Syllable COUNT must also match, so a one-syllable
 * answer never satisfies a two-syllable word.
 *
 * When either side has no reading, rule 2 cannot be evaluated and matching falls back to exact
 * characters only. That is the honest behaviour: without a reading there is no evidence the user
 * said the right thing, and guessing would silently accept wrong answers.
 */
export function isProducedTokenMatch(
  expected: SentenceDrillToken,
  produced: ProducedToken,
): boolean {
  if (produced.word.length > 0 && produced.word === expected.word) {
    return true;
  }
  if (expected.pinyin === null || produced.pinyin === null) {
    return false;
  }
  const expectedReading = toneNumbersPinyinForComparison(expected.pinyin);
  const producedReading = toneNumbersPinyinForComparison(produced.pinyin);
  if (expectedReading === null || producedReading === null) {
    return false;
  }
  return expectedReading === producedReading;
}
